import { DataGrid } from '@neo4j-ndl/react';
import type { FleetManagementTypes } from '@nx/state';
import { useColumnPreferences } from '@nx/state';
import { DataGridHelpers } from '@nx/ui';
import type { ColumnDef } from '@tanstack/react-table';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { ColumnPreferencesHeaderCell } from '../../shared/helpers/table-overrides';
import { useColumns } from './hooks/use-columns';
import type { TabKey } from './types';

type DbmsTableProps = {
  dataGridType: TabKey;
  details: FleetManagementTypes.Server[] | FleetManagementTypes.Database[] | FleetManagementTypes.DatabaseInstance[];
  filter: string;
  setFilter: (filter: string) => void;
};

export const DbmsDetailsTable = ({ dataGridType, details, filter, setFilter }: DbmsTableProps) => {
  const columns = useColumns(dataGridType);
  const columnPrefs = useColumnPreferences(
    'dbmsDetails',
    dataGridType,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    columns as typeof dataGridType extends 'SERVERS'
      ? ColumnDef<FleetManagementTypes.Server>[]
      : typeof dataGridType extends 'DATABASES'
        ? ColumnDef<FleetManagementTypes.Database>[]
        : ColumnDef<FleetManagementTypes.DatabaseInstance>[],
    { serverId: false },
  );

  const table = useReactTable({
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    data: details as typeof dataGridType extends 'SERVERS'
      ? FleetManagementTypes.Server[]
      : typeof dataGridType extends 'DATABASES'
        ? FleetManagementTypes.Database[]
        : FleetManagementTypes.DatabaseInstance[],
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    columns: columns as typeof dataGridType extends 'SERVERS'
      ? ColumnDef<FleetManagementTypes.Server>[]
      : typeof dataGridType extends 'DATABASES'
        ? ColumnDef<FleetManagementTypes.Database>[]
        : ColumnDef<FleetManagementTypes.DatabaseInstance>[],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setFilter,
    autoResetPageIndex: false,
    columnResizeMode: 'onChange',
    enableColumnPinning: true,
    initialState: {
      sorting: [
        // Sorts the Servers and Databases tables
        { id: 'name', desc: false },
        // Sorts the Database Instance table
        { id: 'serverName', desc: false },
      ],
    },
    state: {
      globalFilter: filter,
      columnOrder: columnPrefs.prefs.columnOrder,
      columnVisibility: columnPrefs.prefs.columnVisibility,
      columnPinning: {
        right: ['actions'],
      },
    },
    onColumnOrderChange: columnPrefs.onColumnOrderChange,
    onColumnVisibilityChange: columnPrefs.onColumnVisibilityChange,
  });

  return (
    <DataGrid
      rootProps={{
        className: `[&_.ndl-data-grid-pinned-cell-right]:!grow [&_[role=columnheader].ndl-data-grid-pinned-cell-right]:border-l [&_[role=columnheader].ndl-data-grid-pinned-cell-right]:border-l-palette-neutral-border-weak`,
      }}
      tableInstance={table}
      components={{
        RowsPerPage: DataGridHelpers.RowsPerPageWithBodyMenuPortal,
        HeaderCell: ColumnPreferencesHeaderCell,
      }}
      styling={{
        headerStyle: 'clean',
        borderStyle: 'horizontal',
      }}
      isKeyboardNavigable={false}
      isAutoResizingColumns={false}
    />
  );
};
